import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  useDeleteStoreManualDayEntry,
  useStoreManualDayEntry,
  useUpsertStoreManualDayEntry,
} from '../../../../hooks';
import {
  buildManualDayEntryRequest,
  EMPTY_MANUAL_DAY_ENTRY_FORM,
  type ManualDayEntryEditability,
  type ManualDayEntryFormValues,
} from '../../manualDayEntry';

export function useManualEntryController({
  storeId,
  businessDate,
  isAllShift,
  isLoadingStores,
  isBothConnected,
  editability,
}: {
  storeId?: string;
  businessDate: string | null;
  isAllShift: boolean;
  isLoadingStores: boolean;
  isBothConnected: boolean;
  editability: ManualDayEntryEditability;
}) {
  const [manualFormValues, setManualFormValues] = useState<ManualDayEntryFormValues>(
    EMPTY_MANUAL_DAY_ENTRY_FORM
  );

  const shouldFetchManualDayEntry = Boolean(
    !isLoadingStores &&
      !isBothConnected &&
      isAllShift &&
      storeId &&
      businessDate &&
      (editability.lotto || editability.collections)
  );

  const { data: manualDayEntry, isLoading: isLoadingManualDayEntry } = useStoreManualDayEntry(
    storeId,
    businessDate,
    shouldFetchManualDayEntry
  );

  useEffect(() => {
    if (!shouldFetchManualDayEntry) {
      setManualFormValues(EMPTY_MANUAL_DAY_ENTRY_FORM);
      return;
    }

    setManualFormValues({
      lottoSalesAmount:
        manualDayEntry?.lotto_sales_amount != null ? String(manualDayEntry.lotto_sales_amount) : '',
      lottoPayoutsAmount:
        manualDayEntry?.lotto_payouts_amount != null ? String(manualDayEntry.lotto_payouts_amount) : '',
      cashCollectionAmount:
        manualDayEntry?.cash_collection_amount != null ? String(manualDayEntry.cash_collection_amount) : '',
      creditCardCollectionAmount:
        manualDayEntry?.credit_card_collection_amount != null
          ? String(manualDayEntry.credit_card_collection_amount)
          : '',
    });
  }, [manualDayEntry, shouldFetchManualDayEntry, businessDate]);

  const upsertManualDayEntryMutation = useUpsertStoreManualDayEntry(storeId, businessDate);
  const deleteManualDayEntryMutation = useDeleteStoreManualDayEntry(storeId, businessDate);

  const isSavingManualDayEntry = useMemo(
    () => upsertManualDayEntryMutation.isPending || deleteManualDayEntryMutation.isPending,
    [deleteManualDayEntryMutation.isPending, upsertManualDayEntryMutation.isPending]
  );

  const handleManualFieldChange = useCallback(
    (field: keyof ManualDayEntryFormValues, nextValue: string) => {
      if (!/^\d*(\.\d{0,2})?$/.test(nextValue)) {
        return;
      }

      setManualFormValues((prev) => ({
        ...prev,
        [field]: nextValue,
      }));
    },
    []
  );

  const getManualAmountValue = useCallback((value: string) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }, []);

  const handleSaveManualEntries = useCallback(async () => {
    if (!storeId || !businessDate) return;

    const request = buildManualDayEntryRequest(businessDate, manualFormValues, editability);

    try {
      if (request.type === 'delete') {
        await deleteManualDayEntryMutation.mutateAsync();
        return;
      }

      await upsertManualDayEntryMutation.mutateAsync(request.payload);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to save manual lottery values');
    }
  }, [
    businessDate,
    deleteManualDayEntryMutation,
    editability,
    manualFormValues,
    storeId,
    upsertManualDayEntryMutation,
  ]);

  const handleRemoveManualEntries = useCallback(async () => {
    if (!shouldFetchManualDayEntry) return;

    try {
      await deleteManualDayEntryMutation.mutateAsync();
      setManualFormValues(EMPTY_MANUAL_DAY_ENTRY_FORM);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to remove manual lottery values');
    }
  }, [deleteManualDayEntryMutation, shouldFetchManualDayEntry]);

  return {
    shouldFetchManualDayEntry,
    manualDayEntry,
    isLoadingManualDayEntry,
    isSavingManualDayEntry,
    manualFormValues,
    setManualFormValues,
    handleManualFieldChange,
    getManualAmountValue,
    handleSaveManualEntries,
    handleRemoveManualEntries,
  };
}

